/**
 * DescriptiveFeedback — shows structured AI feedback for descriptive answers.
 * Handles 3 formats:
 *  1. Plain string (legacy mock-test format)
 *  2. Object from mock_tests.py:  { summary, good_highlights, improvement_highlights }
 *  3. Object from server.py evaluate: { clarity, factual, missing, ideal_answer, good_highlights, improvement_highlights }
 */
import React from "react";
import { CheckCircle2, AlertTriangle, Quote } from "lucide-react";
import MathText from "./MathText";

function HighlightCard({ item, type, isDark }) {
  // type: "good" | "improve"
  const isGood = type === "good";
  return (
    <div className={`rounded-xl border p-3 ${isGood
        ? isDark ? "border-emerald-700/50 bg-emerald-900/20" : "border-emerald-300 bg-emerald-50"
        : isDark ? "border-amber-700/50 bg-amber-900/20" : "border-amber-300 bg-amber-50"
      }`}>
      {item.quote && (
        <div className={`flex gap-2 mb-2 ${isGood
            ? isDark ? "text-emerald-300" : "text-emerald-700"
            : isDark ? "text-amber-300" : "text-amber-700"
          }`}>
          <Quote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-70" />
          <p className={`text-sm italic border-l-2 pl-2 ${isGood
              ? isDark ? "border-emerald-600" : "border-emerald-400"
              : isDark ? "border-amber-600" : "border-amber-400"
            }`}>
            "{item.quote}"
          </p>
        </div>
      )}
      <p className={`text-xs leading-relaxed ${isGood
          ? isDark ? "text-emerald-200" : "text-emerald-800"
          : isDark ? "text-amber-200" : "text-amber-800"
        }`}>
        {isGood ? item.reason : item.suggestion}
      </p>
    </div>
  );
}

export default function DescriptiveFeedback({ feedback, isDark = true }) {
  // ── Plain string (old format) ──────────────────────────────────────────────
  if (!feedback || typeof feedback === "string") {
    if (!feedback) return null;
    return (
      <p className={`text-sm ${isDark ? "text-amber-300" : "text-amber-700"}`}>{feedback}</p>
    );
  }

  const goodHighlights = feedback.good_highlights || [];
  const improvements = feedback.improvement_highlights || [];
  const summary = feedback.summary || feedback.missing || "";
  const hasHighlights = goodHighlights.length > 0 || improvements.length > 0;

  // If no highlights at all, just show summary
  if (!hasHighlights && !summary) return null;

  return (
    <div className="space-y-3">
      {/* Summary line */}
      {summary && (
        <p className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
          <MathText text={summary} />
        </p>
      )}

      {/* Good highlights */}
      {goodHighlights.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">
              What You Got Right
            </span>
          </div>
          {goodHighlights.map((h, i) => (
            <HighlightCard key={i} item={h} type="good" isDark={isDark} />
          ))}
        </div>
      )}

      {/* Improvement highlights */}
      {improvements.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">
              Improvement Needed
            </span>
          </div>
          {improvements.map((h, i) => (
            <HighlightCard key={i} item={h} type="improve" isDark={isDark} />
          ))}
        </div>
      )}
    </div>
  );
}
